// Referral program — client helpers (Phase 3, money-free).
//
// Attribution model (confirmed): manual code at signup + shareable link
// aivyv.com/r/{code} (querystring → localStorage). Native deep link is v1.x.
//
// The actual R$5/R$5 credit grant is NOT here — it is a server-side step
// (referral-credit-grant edge fn) held until the credit mechanism is approved.

import { supabase } from "@/integrations/supabase/client";

/** Where a captured code from /r/{code} is stashed until signup completes. */
export const REFERRAL_STORAGE_KEY = "vyv_referral_code";

/** Public base for shareable links. */
const REFERRAL_LINK_BASE = "https://aivyv.com/r/";

/** Codes are 7 chars, unambiguous uppercase alnum (see gen_referral_code SQL). */
const CODE_RE = /^[A-Z2-9]{4,12}$/;

/** Normalize user/URL input to the canonical code form. */
export function normalizeReferralCode(raw: string | null | undefined): string {
  return (raw ?? "").trim().toUpperCase();
}

/** Loose client-side shape check (server is authoritative via claim_referral). */
export function isPlausibleReferralCode(raw: string | null | undefined): boolean {
  return CODE_RE.test(normalizeReferralCode(raw));
}

/** Build the shareable link for a code. */
export function buildReferralLink(code: string): string {
  return REFERRAL_LINK_BASE + normalizeReferralCode(code);
}

/** Persist a captured code (from the /r/{code} deep-link route). */
export function storeReferralCode(code: string): void {
  try {
    const norm = normalizeReferralCode(code);
    if (isPlausibleReferralCode(norm)) {
      localStorage.setItem(REFERRAL_STORAGE_KEY, norm);
    }
  } catch {
    /* ignore storage failures */
  }
}

/** Read a previously captured code (if any). */
export function readStoredReferralCode(): string | null {
  try {
    const v = localStorage.getItem(REFERRAL_STORAGE_KEY);
    return v && isPlausibleReferralCode(v) ? v : null;
  } catch {
    return null;
  }
}

/** Clear the captured code once it has been claimed (or is no longer needed). */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Fetch (creating on first call) the current user's referral code.
 * Returns null on failure — the UI degrades gracefully.
 */
export async function getOrCreateMyReferralCode(): Promise<string | null> {
  try {
    // RPC is not in the generated types until the migration applies in prod;
    // cast mirrors the existing not-yet-typed-table pattern across the app.
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string) => Promise<{ data: unknown; error: unknown }>;
    }).rpc("get_or_create_my_referral_code");
    if (error) {
      console.error("[referral] get_or_create_my_referral_code failed", error);
      return null;
    }
    return typeof data === "string" ? data : null;
  } catch (e) {
    console.error("[referral] getOrCreateMyReferralCode threw", e);
    return null;
  }
}

export type ClaimResult =
  | "ok"
  | "self"
  | "not_found"
  | "already_referred"
  | "cap_reached"
  | "error";

/**
 * Record attribution for the just-signed-up user. Best-effort and idempotent
 * server-side (one attribution per referred user). NEVER blocks the signup UX.
 */
export async function claimReferral(code: string): Promise<ClaimResult> {
  const norm = normalizeReferralCode(code);
  if (!isPlausibleReferralCode(norm)) return "not_found";
  try {
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    }).rpc("claim_referral", { p_code: norm });
    if (error) {
      console.error("[referral] claim_referral failed", error);
      return "error";
    }
    return (data as ClaimResult) ?? "error";
  } catch (e) {
    console.error("[referral] claimReferral threw", e);
    return "error";
  }
}
